/** CCS Insights — public thought-leadership articles. */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowRight, Clock } from "lucide-react";
import api from "@/lib/api";
import { LogoMark } from "@/components/brand/Logo";
import LegalFooter from "@/components/brand/LegalFooter";

const EASE = [0.22, 1, 0.36, 1];

export const InsightsHeader = () => (
  <header className="border-b border-[#EBE3D2] bg-[#FDFBF5]/90 backdrop-blur-xl sticky top-0 z-30">
    <div className="max-w-7xl mx-auto px-6 sm:px-12 h-16 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-3" data-testid="insights-header-logo">
        <LogoMark size={40} />
        <div className="leading-tight">
          <div className="font-display font-semibold text-[#2A1810] text-sm">The CCS Approach</div>
          <div className="text-[9px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">CCS Insights</div>
        </div>
      </Link>
      <nav className="flex items-center gap-5 text-sm">
        <Link to="/" className="text-[#6B5944] hover:text-[#5D3A1F]" data-testid="insights-nav-home">Home</Link>
        <Link to="/spotlight" className="hidden sm:inline text-[#6B5944] hover:text-[#5D3A1F]" data-testid="insights-nav-spotlight">Spotlight</Link>
        <Link to="/book" className="hidden sm:inline text-[#6B5944] hover:text-[#5D3A1F]" data-testid="insights-nav-explore">Explore</Link>
      </nav>
    </div>
  </header>
);

const InsightCard = ({ a, i, featured = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.6, delay: (i % 3) * 0.1, ease: EASE }}
    className={featured ? "md:col-span-2 lg:col-span-3" : ""}
  >
    <Link to={`/insights/${a.id}`} className="group block" data-testid={`insight-card-${a.id}`}>
      {a.image_url && (
        <div className="overflow-hidden rounded-sm border border-[#2A1810]/5 bg-[#F6F3EB]">
          <img
            src={a.image_url}
            alt={a.title}
            className={`w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${featured ? "aspect-[21/9]" : "aspect-[4/3]"}`}
          />
        </div>
      )}
      <div className="mt-5">
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.16em] font-bold text-[#C28A2E]">
          <span>{a.category}</span>
          <span className="w-1 h-1 rounded-full bg-[#C28A2E]/60" />
          <span className="text-[#6B5944] font-semibold normal-case tracking-normal inline-flex items-center gap-1">
            <Clock size={12} strokeWidth={1.5} /> {a.reading_time} min
          </span>
        </div>
        <h3 className={`mt-3 font-display font-medium text-[#2A1810] leading-snug transition-colors duration-300 group-hover:text-[#C28A2E] ${featured ? "text-2xl sm:text-3xl max-w-3xl" : "text-xl"}`}>
          {a.title}
        </h3>
        <p className={`mt-2 text-sm text-[#6B5944] leading-relaxed ${featured ? "max-w-2xl" : "line-clamp-2"}`}>{a.summary}</p>
        <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-[#5D3A1F] group-hover:text-[#C28A2E] transition-colors duration-300">
          Read <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  </motion.div>
);

export default function InsightsPage() {
  const [insights, setInsights] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/public/insights")
      .then((r) => {
        setInsights(r.data.insights || []);
        setCategories(r.data.categories || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = insights;
    if (category !== "All") list = list.filter((a) => a.category === category);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((a) => [a.title, a.summary, a.content].join(" ").toLowerCase().includes(needle));
    }
    return list;
  }, [insights, category, q]);

  return (
    <div className="min-h-screen bg-[#FDFBF5]" data-testid="insights-page">
      <InsightsHeader />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 pt-16 sm:pt-24 pb-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
          <div className="text-xs sm:text-sm uppercase tracking-[0.3em] font-semibold text-[#C28A2E]">Thoughts worth pausing for</div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-[#2A1810]" data-testid="insights-hero-title">
            CCS Insights
          </h1>
          <p className="mt-5 text-base sm:text-lg leading-[1.85] text-[#2A1810]/75 max-w-2xl">
            Reflections on communication, collaboration and service — written to help you lead,
            serve and work with greater intention.
          </p>
        </motion.div>

        {/* Filter + search */}
        <div className="mt-12 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3" data-testid="insights-category-filter">
            {["All", ...categories].map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`text-sm pb-1.5 border-b-2 transition-colors duration-300 ${
                  category === c ? "border-[#C28A2E] text-[#2A1810] font-semibold" : "border-transparent text-[#2A1810]/50 hover:text-[#2A1810]"
                }`}
                data-testid={`insights-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="relative w-full lg:w-64">
            <Search size={16} strokeWidth={1.5} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#6B5944]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search insights…"
              className="w-full bg-transparent border-0 border-b border-[#2A1810]/20 rounded-none focus:border-[#C28A2E] focus:outline-none focus:ring-0 pl-7 py-2.5 text-sm text-[#2A1810] placeholder:text-[#2A1810]/35"
              data-testid="insights-search-input"
            />
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 pb-20 sm:pb-28">
        {loading ? (
          <div className="py-24 text-center text-[#6B5944]">Loading…</div>
        ) : filtered.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16" data-testid="insights-grid">
            {filtered.map((a, i) => (
              <InsightCard key={a.id} a={a} i={i} featured={i === 0 && category === "All" && !q} />
            ))}
          </div>
        ) : (
          <div className="py-20 text-center border border-dashed border-[#EBE3D2] rounded-2xl" data-testid="insights-empty">
            <p className="text-[#6B5944]">
              {q || category !== "All" ? "No insights match your search just yet." : "The first insight is being written."}
            </p>
          </div>
        )}
      </section>

      <footer className="border-t border-[#EBE3D2] py-10 px-6 text-center">
        <p className="text-xs italic text-[#8B5A2B]">"Let everything you do be done in love." — 1 Corinthians 16:14</p>
        <LegalFooter className="mt-6" />
        <p className="mt-2 text-[11px] text-[#6B5944]/70">© The CCS Approach Consultancy Services · Communicate · Collaborate · Serve</p>
      </footer>
    </div>
  );
}
